import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { FileText, ArrowLeft, RefreshCw, Printer, Search, Plus, Pencil, Ban, X, Loader2, Save, AlertTriangle } from "lucide-react";
import RecetaFormModal from "../components/RecetaFormModal";
import { imprimirReceta } from "../lib/recetaPrint";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg border border-white/10";
const INPUT = "bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";
const hoy = () => new Date().toISOString().slice(0, 10);

export default function RecetarioPage() {
  const { token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const navigate = useNavigate();
  const logo = activeEmpresaPropia?.slug || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);
  const empresaNombre = activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "";
  const puedeCrear = hasPermission("recetario.crear");
  const puedeEditar = hasPermission("recetario.editar");
  const puedeAnular = hasPermission("recetario.anular");
  const puedeImprimir = hasPermission("recetario.imprimir") || hasPermission("recetario.ver");
  const puedeIng = hasPermission("recetario.gestionar_ingenieros");
  const puedeChacras = hasPermission("recetario.gestionar_chacras");

  const [tab, setTab] = useState("recetas");
  const [loading, setLoading] = useState(false);
  const [recetas, setRecetas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [ingenieros, setIngenieros] = useState([]);
  const [chacras, setChacras] = useState([]);
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [estado, setEstado] = useState("");
  const [formReceta, setFormReceta] = useState(null);   // {modo:"pendiente"|"editar", factura?, receta?}
  const [anulando, setAnulando] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [ingForm, setIngForm] = useState(null);
  const [chacraForm, setChacraForm] = useState(null);

  const qs = (extra = {}) => new URLSearchParams({ logo_tipo: logo, ...extra }).toString();
  const get = (path) => fetch(`${API}${path}`, { headers }).then(r => r.ok ? r.json() : []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const rp = {};
      if (q) rp.q = q; if (desde) rp.desde = desde; if (hasta) rp.hasta = hasta; if (estado) rp.estado = estado;
      const [r, p, i, c] = await Promise.all([
        hasPermission("recetario.ver") ? get(`/admin/recetario/recetas?${qs(rp)}`) : [],
        hasPermission("recetario.ver") ? get(`/admin/recetario/pendientes?${qs()}`) : [],
        get(`/admin/recetario/ingenieros?${qs({ incluir_inactivos: "true" })}`),
        get(`/admin/recetario/chacras?${qs()}`),
      ]);
      setRecetas(r); setPendientes(p); setIngenieros(i); setChacras(c);
    } catch { toast.error("No se pudo cargar el recetario"); }
    setLoading(false);
    // eslint-disable-next-line
  }, [logo, q, desde, hasta, estado, token]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrirPendiente = (fac) => {
    // productos controlados de la venta + ficha
    const items = (fac.conceptos || []).map(c => ({ producto_id: c.producto_id, descripcion: c.descripcion, unidad: c.unidad || "", cantidad: c.cantidad, ficha: c.ficha_agronomica || {} }));
    setFormReceta({ modo: "pendiente", factura: fac, items });
  };

  const guardarReceta = async (payload) => {
    if (formReceta.modo === "pendiente") {
      const r = await fetch(`${API}/admin/recetario/recetas`, { method: "POST", headers, body: JSON.stringify({ ...payload, logo_tipo: logo, factura_id: formReceta.factura.id }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "No se pudo emitir la receta");
      toast.success(`Receta ${d.numero} emitida`);
      if (puedeImprimir) imprimirReceta(d, empresaNombre);
    } else {
      const r = await fetch(`${API}/admin/recetario/recetas/${formReceta.receta.id}`, { method: "PUT", headers, body: JSON.stringify(payload) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "No se pudo guardar");
      toast.success("Receta actualizada");
    }
    setFormReceta(null); cargar();
  };

  const anular = async () => {
    if (!motivo.trim()) return toast.error("Indicá el motivo");
    const r = await fetch(`${API}/admin/recetario/recetas/${anulando.id}/anular`, { method: "POST", headers, body: JSON.stringify({ motivo }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return toast.error(d.detail || "No se pudo anular");
    toast.success("Receta anulada"); setAnulando(null); setMotivo(""); cargar();
  };

  const guardarIng = async () => {
    const url = ingForm.id ? `${API}/admin/recetario/ingenieros/${ingForm.id}` : `${API}/admin/recetario/ingenieros`;
    const r = await fetch(url, { method: ingForm.id ? "PUT" : "POST", headers, body: JSON.stringify({ ...ingForm, logo_tipo: logo }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return toast.error(d.detail || "No se pudo guardar");
    toast.success("Ingeniero guardado"); setIngForm(null); cargar();
  };
  const toggleIng = async (i) => {
    await fetch(`${API}/admin/recetario/ingenieros/${i.id}`, { method: "PUT", headers, body: JSON.stringify({ activo: i.activo === false }) });
    cargar();
  };
  const guardarChacra = async () => {
    const r = await fetch(`${API}/admin/recetario/chacras/${chacraForm.id}`, { method: "PUT", headers, body: JSON.stringify(chacraForm) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return toast.error(d.detail || "No se pudo guardar");
    toast.success("Chacra actualizada"); setChacraForm(null); cargar();
  };
  const desactivarChacra = async (c) => {
    await fetch(`${API}/admin/recetario/chacras/${c.id}`, { method: "PUT", headers, body: JSON.stringify({ activo: false }) });
    cargar();
  };

  const TABS = [["recetas", "Recetas"], ["pendientes", `Ventas sin receta${pendientes.length ? ` (${pendientes.length})` : ""}`], ["ingenieros", "Ingenieros"], ["chacras", "Chacras"]];

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1 -ml-1 rounded-lg hover:bg-white/5"><ArrowLeft className="w-5 h-5" /></button>
          <FileText className="w-6 h-6 text-lime-400" />
          <div><h1 className="text-lg font-bold">Recetario agronómico</h1><p className="text-xs text-slate-400">{activeEmpresaPropia?.nombre || ""} — Recetas / órdenes de aplicación</p></div>
        </div>
        <button onClick={cargar} className={BTN_SEC}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
      </div>

      <div className="px-6 pt-4 flex gap-1 border-b border-white/10">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm rounded-t-lg ${tab === k ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      <div className="p-6">
        {tab === "recetas" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="relative"><Search className="w-4 h-4 text-slate-500 absolute left-3 top-3" /><input className={`${INPUT} pl-9 w-64`} placeholder="Buscar N°, cliente, chacra, cultivo…" value={q} onChange={e => setQ(e.target.value)} /></div>
              <div><label className={LABEL}>Desde</label><DateInput className={INPUT} value={desde} onChange={e => setDesde(e.target.value)} /></div>
              <div><label className={LABEL}>Hasta</label><DateInput className={INPUT} value={hasta} onChange={e => setHasta(e.target.value)} /></div>
              <div><label className={LABEL}>Estado</label><select className={INPUT} value={estado} onChange={e => setEstado(e.target.value)}><option value="">Todas</option><option value="emitida">Emitidas</option><option value="anulada">Anuladas</option></select></div>
            </div>
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400"><tr><th className="text-left px-3 py-2">N°</th><th className="text-left px-3 py-2">Fecha</th><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Chacra / cultivo</th><th className="text-left px-3 py-2">Ingeniero</th><th className="text-left px-3 py-2">Venta</th><th className="px-3 py-2"></th></tr></thead>
                <tbody>
                  {recetas.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-8">Sin recetas</td></tr>}
                  {recetas.map(r => (
                    <tr key={r.id} className={`border-t border-white/5 ${r.estado === "anulada" ? "opacity-50" : ""}`}>
                      <td className="px-3 py-2 font-mono">{r.numero}{r.estado === "anulada" && <span className="ml-2 text-[10px] text-red-400">ANULADA</span>}{r.factura_anulada && <span className="ml-2 text-[10px] text-amber-400">VENTA ANULADA</span>}</td>
                      <td className="px-3 py-2">{r.fecha_emision}</td>
                      <td className="px-3 py-2">{r.cliente_nombre}</td>
                      <td className="px-3 py-2">{r.chacra_nombre} <span className="text-slate-500">({r.superficie_ha} ha · {r.cultivo})</span></td>
                      <td className="px-3 py-2">{r.ingeniero_nombre}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.factura_numero || "—"}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {puedeImprimir && <button title="Imprimir" onClick={() => imprimirReceta(r, empresaNombre)} className="p-1.5 text-slate-400 hover:text-white"><Printer className="w-4 h-4" /></button>}
                        {puedeEditar && r.estado !== "anulada" && <button title="Editar" onClick={() => setFormReceta({ modo: "editar", receta: r, items: r.items })} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>}
                        {puedeAnular && r.estado !== "anulada" && <button title="Anular" onClick={() => { setAnulando(r); setMotivo(""); }} className="p-1.5 text-slate-400 hover:text-red-400"><Ban className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "pendientes" && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Ventas con productos que requieren receta y que todavía no tienen una receta emitida (por ejemplo, anteriores a habilitar el módulo).</p>
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400"><tr><th className="text-left px-3 py-2">Venta</th><th className="text-left px-3 py-2">Fecha</th><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Productos controlados</th><th className="px-3 py-2"></th></tr></thead>
                <tbody>
                  {pendientes.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-8">No hay ventas sin receta</td></tr>}
                  {pendientes.map(f => (
                    <tr key={f.id} className="border-t border-white/5">
                      <td className="px-3 py-2 font-mono text-xs">{f.numero || f.numero_boleta}</td><td className="px-3 py-2">{f.fecha}</td><td className="px-3 py-2">{f.razon_social}</td>
                      <td className="px-3 py-2 text-slate-300">{(f.conceptos || []).map(c => `${c.descripcion} (${c.cantidad})`).join(", ")}</td>
                      <td className="px-3 py-2 text-right">{puedeCrear && <button onClick={() => abrirPendiente(f)} className={BTN_PRI}><Plus className="w-4 h-4" />Emitir receta</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "ingenieros" && (
          <div className="space-y-3">
            {puedeIng && <button onClick={() => setIngForm({ nombre: "", matricula: "", ci: "", telefono: "", email: "" })} className={BTN_PRI}><Plus className="w-4 h-4" />Nuevo ingeniero</button>}
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400"><tr><th className="text-left px-3 py-2">Nombre</th><th className="text-left px-3 py-2">Matrícula</th><th className="text-left px-3 py-2">C.I.</th><th className="text-left px-3 py-2">Contacto</th><th className="px-3 py-2"></th></tr></thead>
                <tbody>
                  {ingenieros.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-8">Sin ingenieros cargados</td></tr>}
                  {ingenieros.map(i => (
                    <tr key={i.id} className={`border-t border-white/5 ${i.activo === false ? "opacity-50" : ""}`}>
                      <td className="px-3 py-2">{i.nombre}{i.activo === false && <span className="ml-2 text-[10px] text-slate-500">INACTIVO</span>}</td><td className="px-3 py-2">{i.matricula}</td><td className="px-3 py-2">{i.ci}</td><td className="px-3 py-2 text-slate-400">{[i.telefono, i.email].filter(Boolean).join(" · ")}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{puedeIng && <><button onClick={() => setIngForm(i)} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button><button onClick={() => toggleIng(i)} className="p-1.5 text-slate-400 hover:text-amber-400" title={i.activo === false ? "Reactivar" : "Desactivar"}><Ban className="w-4 h-4" /></button></>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "chacras" && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Las chacras se guardan solas la primera vez que se emite una receta para un cliente. Acá podés corregirlas o desactivarlas.</p>
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400"><tr><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Chacra</th><th className="text-right px-3 py-2">Ha</th><th className="text-left px-3 py-2">Coordenadas</th><th className="px-3 py-2"></th></tr></thead>
                <tbody>
                  {chacras.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-8">Sin chacras</td></tr>}
                  {chacras.map(c => (
                    <tr key={c.id} className="border-t border-white/5"><td className="px-3 py-2">{c.cliente_nombre}</td><td className="px-3 py-2">{c.nombre}</td><td className="px-3 py-2 text-right">{c.superficie_ha}</td><td className="px-3 py-2 text-slate-400">{c.coordenadas}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{puedeChacras && <><button onClick={() => setChacraForm(c)} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button><button onClick={() => desactivarChacra(c)} className="p-1.5 text-slate-400 hover:text-red-400"><Ban className="w-4 h-4" /></button></>}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <RecetaFormModal
        open={!!formReceta} onClose={() => setFormReceta(null)} token={token} logoTipo={logo}
        cliente={formReceta?.factura ? { id: formReceta.factura.empresa_id, nombre: formReceta.factura.razon_social, ruc: formReceta.factura.ruc } : null}
        items={formReceta?.items || []} initial={formReceta?.modo === "editar" ? formReceta.receta : null}
        title={formReceta?.modo === "editar" ? `Editar receta ${formReceta.receta?.numero}` : "Emitir receta para la venta"}
        confirmLabel={formReceta?.modo === "editar" ? "Guardar cambios" : "Emitir receta"}
        puedeCrearIngeniero={puedeIng} onConfirm={guardarReceta}
      />

      {anulando && (
        <div className="fixed inset-0 bg-black/80 z-[90] flex items-center justify-center p-4"><div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-sm p-5">
          <h3 className="font-semibold mb-1">Anular receta {anulando.numero}</h3><p className="text-xs text-slate-400 mb-3">Queda registrada con el motivo; no se borra.</p>
          <input className={`${INPUT} w-full`} placeholder="Motivo" value={motivo} onChange={e => setMotivo(e.target.value)} />
          <div className="flex justify-end gap-2 mt-4"><button onClick={() => setAnulando(null)} className="px-3 py-2 text-sm text-slate-400">Cancelar</button><button onClick={anular} className="px-3 py-2 text-sm bg-red-600 hover:bg-red-700 rounded-lg">Anular</button></div>
        </div></div>
      )}

      {ingForm && (
        <div className="fixed inset-0 bg-black/80 z-[90] flex items-center justify-center p-4"><div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-3">
          <h3 className="font-semibold">{ingForm.id ? "Editar ingeniero" : "Nuevo ingeniero agrónomo"}</h3>
          {[["nombre", "Nombre *"], ["matricula", "Matrícula *"], ["ci", "C.I."], ["telefono", "Teléfono"], ["email", "Email"]].map(([k, l]) => (
            <div key={k}><label className={LABEL}>{l}</label><input className={`${INPUT} w-full`} value={ingForm[k] || ""} onChange={e => setIngForm(p => ({ ...p, [k]: e.target.value }))} /></div>
          ))}
          <div className="flex justify-end gap-2"><button onClick={() => setIngForm(null)} className="px-3 py-2 text-sm text-slate-400">Cancelar</button><button onClick={guardarIng} className={BTN_PRI}><Save className="w-4 h-4" />Guardar</button></div>
        </div></div>
      )}

      {chacraForm && (
        <div className="fixed inset-0 bg-black/80 z-[90] flex items-center justify-center p-4"><div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-3">
          <h3 className="font-semibold">Editar chacra — {chacraForm.cliente_nombre}</h3>
          {[["nombre", "Nombre"], ["superficie_ha", "Superficie (ha)"], ["coordenadas", "Coordenadas"], ["localidad", "Localidad"]].map(([k, l]) => (
            <div key={k}><label className={LABEL}>{l}</label><input className={`${INPUT} w-full`} value={chacraForm[k] ?? ""} onChange={e => setChacraForm(p => ({ ...p, [k]: e.target.value }))} /></div>
          ))}
          <div className="flex justify-end gap-2"><button onClick={() => setChacraForm(null)} className="px-3 py-2 text-sm text-slate-400">Cancelar</button><button onClick={guardarChacra} className={BTN_PRI}><Save className="w-4 h-4" />Guardar</button></div>
        </div></div>
      )}
    </div>
  );
}
